<script setup lang="ts">
import { faCopy, faFilePen } from '@fortawesome/pro-duotone-svg-icons'
import { MARKING_COLORS } from '~/utils/markings'

interface Props {
  /** Container selector where text selection should be monitored */
  containerKeys?: Array<ContainerKey> | ReadonlyArray<ContainerKey>
}

const props = withDefaults(defineProps<Props>(), {
  containerKeys: () => CONTAINER_KEYS,
})

const {
  capturedRange,
  copySelection,
  hideTooltip,
  selectedTextData,
  selectionRanges,
  showTooltip,
  showTooltipForRange,
  tooltipCoords,
  tooltipRef,
} = useSelectionTooltip(() => props.containerKeys)

const { activeMarkingColor, handleColorClick, handleNoteClick } = useSelectionMarkings({
  containerKeys: () => props.containerKeys,
  capturedRange,
  selectionRanges,
  selectedTextData,
  tooltipCoords,
  hideTooltip,
  showTooltipForRange,
})
</script>

<template>
  <div
    v-if="showTooltip && selectedTextData"
    id="selection-tooltip"
    ref="tooltipRef"
    class="fixed z-[70] flex items-stretch bg-surface-elevated reading-dark:bg-carbon-500 rounded-lg shadow-lg select-none gap-x-3xs px-3xs py-3xs"
    :style="{
      top: `${tooltipCoords.y}px`,
      left: `${tooltipCoords.x}px`,
    }"
  >
    <div id="mark-btn" class="flex items-center gap-1.5 px-xs">
      <MarkingsColorSwatch
        v-for="color in MARKING_COLORS"
        :key="color"
        :color="color"
        :label="`Marker med ${color}`"
        :selected="activeMarkingColor === color"
        removable
        @mousedown.prevent
        @click="handleColorClick(color)"
      />
    </div>

    <Divider orientation="vertical" no-margin class="self-stretch" />

    <ControlButton
      id="copy-with-source-btn"
      size="small"
      type="ghost"
      pinned
      :toggle-selected="false"
      class="reading-dark:hover:bg-surface-secondary reading-dark:focus-visible:bg-surface-secondary"
      aria-label="Kopier den valgte teksten med kildeangivelse"
      @mousedown.prevent
      @click="copySelection"
      @keydown.enter="copySelection"
      @keydown.space.prevent="copySelection"
    >
      <Icon :icon="faCopy" class="shrink-0 text-primary" />
      <span class="whitespace-nowrap text-carbon-500 reading-dark:text-primary"
        >Kopier med kilde</span
      >
    </ControlButton>

    <Divider orientation="vertical" no-margin class="self-stretch reading-dark:carbon-400" />

    <ControlButton
      id="note-btn"
      size="small"
      type="ghost"
      pinned
      :toggle-selected="false"
      class="reading-dark:hover:bg-surface-secondary reading-dark:focus-visible:bg-surface-secondary"
      aria-label="Legg til notat"
      @mousedown.prevent
      @click="handleNoteClick"
    >
      <Icon :icon="faFilePen" class="shrink-0 text-primary" />
      <span class="whitespace-nowrap text-carbon-500 reading-dark:text-primary">Notat</span>
    </ControlButton>
  </div>
</template>
